import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { OpenXAgentChip } from '../../MatchesDetails/shared/OpenXAgentChip.tsx';
import { OpenXAgentChipList } from '../../MatchesDetails/shared/OpenXAgentTitledCard.tsx';
import { OpenXAgentWidgetError } from '../components/OpenXAgentWidgetError.tsx';
import { STATUS_ICONS, StatusTone } from '../utils/statusTonePalette.ts';
import { columnSx } from '../utils/styles.ts';
import { useParsedWidget } from '../utils/useParsedWidget.ts';
import { chipSx, emphasisSx, groupLabelSx, srOnlySx } from './OpenXAgentTargetingChips.styles.ts';
import {
  getTargetingChipTone,
  parseTargetingChipsPayload,
  type TargetingChipItem,
} from './utils/targetingChipsPayload.ts';

type OpenXAgentTargetingChipsProps = {
  payload: unknown;
};

const ChipContent = ({ item }: { item: TargetingChipItem }) => {
  const { t } = useTranslation();
  const isIncluded = getTargetingChipTone(item.status) === StatusTone.POSITIVE;

  return (
    <>
      <Box component="span" sx={srOnlySx}>
        {isIncluded ? t('Included') : t('Excluded')}:{' '}
      </Box>
      {item.value === null ? (
        <Box component="span" sx={emphasisSx}>
          {item.label}
        </Box>
      ) : (
        <>
          {item.label}:{' '}
          <Box component="span" sx={emphasisSx}>
            {item.value}
          </Box>
        </>
      )}
    </>
  );
};

export const OpenXAgentTargetingChips = ({ payload }: OpenXAgentTargetingChipsProps) => {
  const parsed = useParsedWidget(payload, parseTargetingChipsPayload, 'OpenXAgentTargetingChips');

  if (!parsed.ok) return <OpenXAgentWidgetError />;

  return (
    <Box sx={columnSx} data-test="openx-agent-targeting-chips">
      {parsed.value.groups.map(group => (
        <Box component="section" key={group.id}>
          <Typography component="h4" sx={groupLabelSx}>
            {group.label}
          </Typography>
          <OpenXAgentChipList>
            {group.items.map(item => {
              const tone = getTargetingChipTone(item.status);
              return (
                <OpenXAgentChip
                  key={item.id}
                  component="li"
                  icon={STATUS_ICONS[tone]}
                  sx={chipSx(tone)}
                  data-test="targeting-chip"
                >
                  <ChipContent item={item} />
                </OpenXAgentChip>
              );
            })}
          </OpenXAgentChipList>
        </Box>
      ))}
    </Box>
  );
};
